import axios from "axios";
import store from "@/store";
import { ToastService } from "@/services/toastService";

class RequestCancelledError extends Error {
  constructor(message) {
    super(message);
    this.name = "RequestCancelledError";
  }
}

const pendingRequests = new Map();

const removeRequest = (requestKey) => {
  if (pendingRequests.has(requestKey)) pendingRequests.delete(requestKey);
};

export const cancelAllPendingRequests = () => {
  pendingRequests.forEach((cancelToken) => {
    if (cancelToken && typeof cancelToken.cancel === "function") {
      try {
        cancelToken.cancel("Requisição cancelada devido a mudança de rota");
      } catch (error) {
        console.log("Token já cancelado ou inválido");
      }
    }
  });

  pendingRequests.clear();
};

const cepCache = {};

export const instance = () => {
  const createdInstance = axios.create({
    baseURL: process.env.VUE_APP_URL_API_CRM,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
      "X-Access-Token": localStorage.getItem("token"),
    },
  });

  createdInstance.interceptors.request.use(
    (config) => {
      const source = axios.CancelToken.source();

      source.token.config = config;
      config.cancelToken = source.token;

      pendingRequests.set(source.token, source);

      const token = localStorage.getItem("token");

      if (token) config.headers["X-Access-Token"] = token;

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  createdInstance.interceptors.response.use(
    (response) => {
      if (response.config.cancelToken) removeRequest(response.config.cancelToken);

      if (response.status === 200) {
        if (response.data?.warning) ToastService.showWarning(response.data.warning);

        return response.data;
      } else {
        return Promise.reject({
          data: response.data.message,
          status: response.status,
        });
      }
    },
    (error) => {
      if (error.config?.cancelToken) removeRequest(error.config.cancelToken);

      if (axios.isCancel(error)) return Promise.reject(new RequestCancelledError(error.message));

      if (error?.response?.status === 401) {
        store.dispatch("app/logout");
      } else {
        if (error?.response?.data?.warning) ToastService.showWarning(error.response.data.warning);

        ToastService.showError(error);
      }

      return Promise.reject(error);
    },
  );

  return createdInstance;
};

export const imhubInstance = () => {
  const createdInstance = axios.create({
    baseURL: process.env.VUE_APP_IMHUB_API_URL,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  createdInstance.interceptors.request.use(
    (config) => {
      const source = axios.CancelToken.source();

      source.token.config = config;
      config.cancelToken = source.token;

      pendingRequests.set(source.token, source);

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  createdInstance.interceptors.response.use(
    (response) => {
      if (response.config.cancelToken) removeRequest(response.config.cancelToken);

      if (response.status === 200) {
        if (response.data?.warning) ToastService.showWarning(response.data.warning);

        return response.data;
      } else {
        return Promise.reject({
          data: response.data.message,
          status: response.status,
        });
      }
    },
    (error) => {
      if (error.config?.cancelToken) removeRequest(error.config.cancelToken);

      if (axios.isCancel(error)) return Promise.reject(new RequestCancelledError(error.message));

      if (error?.response?.status === 401) {
        store.dispatch("app/logout");
      } else {
        if (error?.response?.data?.warning) ToastService.showWarning(error.response.data.warning);

        ToastService.showError(error);
      }

      return Promise.reject(error);
    },
  );

  return createdInstance;
};

export const getAddressByCep = async (cep) => {
  const cleanCep = cep.replace(/\D/g, "");

  if (cleanCep.length !== 8) {
    throw new Error("CEP inválido");
  }

  if (cepCache[cleanCep]) {
    return cepCache[cleanCep];
  }

  try {
    const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);

    cepCache[cleanCep] = response.data;

    return response.data;
  } catch {
    ToastService.showWarning("Erro ao buscar endereço pelo CEP");
  }
};

export default instance;
